import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  Plus,
  Users,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../ui';
import { useRooms } from '../../features/rooms/useRooms';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { roomsAccessibleTo } from '../../features/rooms/roomModel';
import { useAuth } from '../../hooks/useAuth';
import { CreateRoomModal } from './CreateRoomModal';
import type { Room } from '../../features/rooms/types';

/**
 * Rooms list — one card per room showing member count and default-template
 * status, with a create-room modal. Reads through the useRooms hook only.
 * Clicking a card opens the room detail/settings page.
 */
export function RoomsPage() {
  const navigate = useNavigate();
  const { summaries, loading, error } = useRooms();
  const { activeRoomId, setActiveRoomId } = useActiveRoom();
  const { identity } = useAuth();
  const [creating, setCreating] = useState(false);

  // Prefer rooms the signed-in user can access (created OR is a member of).
  // SAFE FALLBACK: if the user is in none, show every room so no one is locked
  // out. The active room is always kept visible even if not directly owned.
  const visible = useMemo(() => {
    const accessible = roomsAccessibleTo(summaries, identity?.alias ?? '');
    const base = accessible.length > 0 ? [...accessible] : summaries;
    if (activeRoomId && !base.some((r) => r.id === activeRoomId)) {
      const active = summaries.find((r) => r.id === activeRoomId);
      if (active) base.push(active);
    }
    return base;
  }, [summaries, identity, activeRoomId]);

  const newButton = (
    <Button
      leftIcon={<Plus className="h-4 w-4" />}
      onClick={() => setCreating(true)}
    >
      New Room
    </Button>
  );

  const onCreated = (room: Room) => {
    setActiveRoomId(room.id);
    navigate(`/rooms/${encodeURIComponent(room.id)}`);
  };

  return (
    <div>
      <PageHeader
        icon={<Boxes className="h-5 w-5" />}
        title="Rooms"
        description="Workspaces that group tasks, estimates, and conversations for a team or project."
        actions={newButton}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading rooms" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load rooms"
          description={error}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No rooms yet"
          description="Create a room to start tracking work and computing estimates against a template."
          action={newButton}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((room) => {
            const active = room.id === activeRoomId;
            return (
              <Card
                key={room.id}
                interactive
                onClick={() =>
                  navigate(`/rooms/${encodeURIComponent(room.id)}`)
                }
              >
                <CardHeader>
                  <CardTitle>{room.name}</CardTitle>
                  {active && (
                    <Badge tone="success" dot>
                      active
                    </Badge>
                  )}
                </CardHeader>
                <CardBody className="space-y-4">
                  <p className="line-clamp-2 min-h-[2.5rem] text-sm text-zinc-400">
                    {room.description || 'No description provided.'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <Badge tone="neutral">
                      <Users className="mr-1 h-3 w-3" />
                      {room.memberCount}{' '}
                      {room.memberCount === 1 ? 'member' : 'members'}
                    </Badge>
                    {room.defaultTemplateId && (
                      <Badge tone="info">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        default template
                      </Badge>
                    )}
                    {room.createdBy && <span>by {room.createdBy}</span>}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <CreateRoomModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={onCreated}
      />
    </div>
  );
}

export default RoomsPage;
